import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCardsStore, useTopicsStore } from '@/stores'
import { formatLidForDisplay } from '@/utils/lid'
import type { Topic } from '@/types'
import './SearchPage.css'

export default function SearchPage() {
  const [query, setQuery] = useState('')
  const [selectedTopic, setSelectedTopic] = useState('')

  const { cards } = useCardsStore()
  const { topics, getTopicsSorted } = useTopicsStore()
  const sortedTopics = getTopicsSorted()

  // Search logic
  const searchResults = useMemo(() => {
    if (!query.trim()) return []

    const searchTerms = query.toLowerCase().split(' ').filter(Boolean)
    const allCards = Array.from(cards.values())

    return allCards
      .filter((card) => {
        // Filter by topic if selected
        if (selectedTopic && card.topicId !== selectedTopic) {
          return false
        }

        // Search in title, content, and L-ID
        const searchableText = `${card.lid} ${card.title} ${card.content}`.toLowerCase()

        // All search terms must match
        return searchTerms.every((term) => searchableText.includes(term))
      })
      .map((card) => {
        // Calculate relevance score
        let score = 0
        const titleLower = card.title.toLowerCase()
        const lidLower = card.lid.toLowerCase()

        searchTerms.forEach((term) => {
          if (lidLower.includes(term)) score += 10
          if (titleLower.includes(term)) score += 5
        })

        return { card, score }
      })
      .sort((a, b) => b.score - a.score)
      .map((item) => item.card)
  }, [query, selectedTopic, cards])

  const highlightText = (text: string, maxLength = 200) => {
    if (!query.trim()) return text.substring(0, maxLength)

    const searchTerms = query.toLowerCase().split(' ').filter(Boolean)
    let result = text

    // Find first occurrence to show relevant context
    let firstMatch = text.length
    searchTerms.forEach((term) => {
      const index = text.toLowerCase().indexOf(term)
      if (index !== -1 && index < firstMatch) {
        firstMatch = index
      }
    })

    // Extract context around first match
    const start = Math.max(0, firstMatch - 50)
    const end = Math.min(text.length, start + maxLength)
    result = (start > 0 ? '...' : '') + text.substring(start, end) + (end < text.length ? '...' : '')

    return result
  }

  return (
    <div className="search-page">
      <div className="page-header">
        <h1>Search</h1>
        <p className="text-secondary">Find cards by title, content, or L-ID</p>
      </div>

      <div className="search-controls">
        <div className="search-input-wrapper">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="search-icon"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search cards..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button
              className="search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <select
          className="input topic-filter"
          value={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value)}
        >
          <option value="">All topics</option>
          {sortedTopics.map((topic: Topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.id}/ - {topic.name}
            </option>
          ))}
        </select>
      </div>

      {query.trim() ? (
        <div className="search-results">
          <div className="results-header">
            <span className="results-count">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
            </span>
          </div>

          {searchResults.length === 0 ? (
            <div className="empty-state">
              <h3>No results found</h3>
              <p>Try different keywords or remove filters</p>
            </div>
          ) : (
            <ul className="results-list">
              {searchResults.map((card) => {
                const topic = topics.find((t: Topic) => t.id === card.topicId)
                return (
                  <li key={card.lid}>
                    <Link
                      to={`/card/${encodeURIComponent(card.lid)}`}
                      className="result-item card"
                    >
                      <div className="result-header">
                        <span className="lid-badge">{formatLidForDisplay(card.lid)}</span>
                        <span className="result-topic">
                          {topic?.name || `Topic ${card.topicId}`}
                        </span>
                      </div>
                      <h3 className="result-title">{card.title}</h3>
                      <p className="result-excerpt">{highlightText(card.content)}</p>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : (
        <div className="search-tips">
          <h3>Search Tips</h3>
          <ul>
            <li>Search by L-ID: <code>1/1a</code></li>
            <li>Search by title keywords: <code>systems theory</code></li>
            <li>Multiple terms narrow results: <code>communication social</code></li>
            <li>Filter by topic using the dropdown</li>
          </ul>
        </div>
      )}
    </div>
  )
}
